import { useEffect, useState } from 'react'
import { Outlet, useNavigate, useParams } from 'react-router-dom'
import { useJoinStore } from '@/store/joinStore'
import { useJoinInterview } from '@/hooks/useJoinInterview'
import { useInterviewStore } from '@/store/interviewStore'
import { InterviewSessionContext } from '@/context/InterviewSessionContext'
import { connectSession } from '@/api/publicSession'
import type { Language } from '@/types/interview'
import { useJoinEffectiveLanguage } from '@/hooks/useJoinEffectiveLanguage'

export function JoinLayout() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const resolved = useJoinStore((s) => s.resolved)
  const bearer = useJoinStore((s) => s.bearer)
  const token = useJoinStore((s) => s.token)
  const effectiveLanguage = useJoinEffectiveLanguage()
  const livekitConn = useJoinStore((s) => s.livekitConn)
  const setLivekitConn = useJoinStore((s) => s.setLivekitConn)
  const status = useInterviewStore((s) => s.status)
  const aborted = useInterviewStore((s) => s.aborted)
  const config = useInterviewStore((s) => s.config)
  const setConfig = useInterviewStore((s) => s.setConfig)
  const interview = useJoinInterview(sessionId ?? '')
  const [continuing, setContinuing] = useState(false)

  // Mirror resolved → interviewStore.config so screens shared with the
  // candidate flow (InstructionsScreen) pass their `if (!store.config)`
  // guard. Done eagerly, before /connect, because config is derived from
  // the resolved payload and doesn't depend on LiveKit credentials.
  useEffect(() => {
    if (resolved && !config) {
      setConfig({
        language: effectiveLanguage,
        // Avatar is resolved server-side (candidate /select index -> worker); the
        // public flow sends no gender. This placeholder only satisfies the shared
        // InterviewConfig shape and is not read in this path.
        avatarGender: 'female',
        mode: 'full',
      })
    }
  }, [resolved, config, setConfig])

  // When status flips to 'complete', navigate to the thank-you route. The
  // splash there reads `aborted` from the store to decide between the
  // celebratory finish and the neutral "interview ended" copy, so a manual
  // abort isn't told the interview was "successfully completed".
  useEffect(() => {
    if (status === 'complete') {
      navigate(`/j/${sessionId}/thankyou`, { replace: true })
    }
  }, [status, sessionId, navigate])

  const language: Language = effectiveLanguage

  async function handleStart() {
    try {
      await interview.startInterview()
    } catch (err) {
      // getUserMedia failures set store.mediaError inside startInterview;
      // anything else (missing bearer, backend down) just must not escape
      // as an unhandled rejection.
      console.error('Start failed:', err)
    }
  }

  function handleNext() {
    void interview.advance()
  }

  function handleEnd() {
    void interview.endInterview(true)
  }

  function handleContinue() {
    if (!sessionId) return
    // Re-entry guard: if /connect already ran (livekitConn cached) just
    // navigate. /connect is non-idempotent server-side — calling it twice
    // returns 409.
    if (!bearer || livekitConn) {
      navigate(`/j/${sessionId}/interview`)
      return
    }
    if (continuing) return
    const generation = interview.beginConnection()
    const connectionId = useJoinStore.getState().connectionId!
    setContinuing(true)
    // Navigate immediately so the cozy waiting placeholder covers the whole
    // connect/setup wait instead of leaving the user on the instructions
    // screen with no feedback.
    navigate(`/j/${sessionId}/interview`)
    void (async () => {
      try {
        const { livekitUrl, livekitToken } = await connectSession(sessionId, bearer, connectionId)
        if (!interview.isCurrent(generation)) return
        setLivekitConn(livekitUrl, livekitToken)
        // Pass credentials explicitly — setup()'s store closure won't have
        // observed setLivekitConn yet within this tick.
        await interview.setup({ url: livekitUrl, token: livekitToken }, generation)
      } catch (err) {
        if (!interview.isCurrent(generation)) return
        console.warn('connect/setup failed:', err)
        navigate(`/j/${sessionId}/status`, {
          replace: true,
          state: { variant: 'network_error' },
        })
      } finally {
        if (interview.isCurrent(generation)) setContinuing(false)
      }
    })()
  }

  function handleBackToHome() {
    // A manual abort leaves the session resettable server-side: re-opening the
    // same link runs the resolver, which resets the abandoned in_progress
    // attempt back to `created` and drops the candidate on the preferences/
    // avatar screen to start over — matching the abort copy ("reopen the link
    // and start from the beginning"). A full reload (not SPA navigation)
    // re-initializes the in-memory stores, so the stale 'complete' status
    // can't bounce us straight back to the thank-you screen.
    if (aborted && sessionId && token) {
      window.location.href = `/j/${sessionId}?t=${encodeURIComponent(token)}`
      return
    }
    // Natural completion is terminal — go to the template redirect target
    // (or the default) instead.
    window.location.href = resolved?.redirectUrl ?? 'https://www.tlou.ai'
  }

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100">
      <InterviewSessionContext.Provider
        value={{
          language,
          interview,
          // Template-configured post-interview redirect target (public flow).
          // ThankYouSplash falls back to the default when this is null.
          redirectUrl: resolved?.redirectUrl ?? null,
          handleStart,
          handleNext,
          handleEnd,
          handleContinue,
          handleBackToHome,
          // candidate-only handlers intentionally omitted — language/avatar
          // screens are not routed in /j/*.
        }}
      >
        <Outlet />
      </InterviewSessionContext.Provider>
    </div>
  )
}
